import * as React from "react";
import { useButton, type AriaButtonProps } from "@react-aria/button";
import { FocusRing } from "@react-aria/focus";
import { useHover } from "@react-aria/interactions";
import { mergeProps, useObjectRef } from "@react-aria/utils";
import { useAnimation, motion, type HTMLMotionProps } from "framer-motion";
import { twMerge } from "tailwind-merge";
import buttonColors from "../colors";
import { Link, useSearchParams } from "react-router-dom";

type ButtonProps = AriaButtonProps<"button"> & {
  className?: string | undefined;
} & HTMLMotionProps<"button"> & { dangerouslySetStyle?: string | undefined };

const MotionLink = motion(Link);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, forwardedRef) => {
    const {
      onPress,
      onClick,
      className,
      initial,
      children,
      isDisabled,
      dangerouslySetStyle,
      ...delegated
    } = props;
    const controls = useAnimation();
    const ref = useObjectRef(forwardedRef);
    const { hoverProps } = useHover({
      isDisabled,
      onHoverStart: () => {
        controls.stop();
        controls.set({
          background: isDisabled ? buttonColors.disabled : buttonColors.hover,
        });
      },
      onHoverEnd: () => {
        controls.start({
          background: buttonColors.normal,
          transition: { duration: 0.2 },
        });
      },
    });

    const { buttonProps } = useButton(
      {
        onPressStart: () => {
          controls.stop();
          controls.set({ background: buttonColors.press });
        },
        onPressEnd: () => {
          controls.start({
            background: buttonColors.normal,
            transition: { duration: 0.3 },
          });
        },
        onPress: onPress || onClick,
        isDisabled,
      },
      ref
    );

    return (
      <FocusRing focusRingClass="ring-2 ring-sky-600/80 ring-offset-4 ring-offset-white/90	">
        <motion.button
          ref={ref}
          animate={controls}
          {...mergeProps(buttonProps, hoverProps, delegated)}
          style={{
            WebkitTapHighlightColor: "transparent",
            border: "1px solid hsl(0deg 0% 100% / 24%)",
            textShadow: "1px 1px 1px hsl(0deg 0% 10% / 20%)",
            ...dangerouslySetStyle,
          }}
          className={twMerge(
            "shrink-0 touch-none select-none",
            "bg-button-normal text-white shadow-sm shadow-black/30",
            "border-transparent focus:border-transparent",
            "focus:outline-none",
            "disabled:cursor-not-allowed",
            "text-center",
            className
          )}
        >
          {children}
        </motion.button>
      </FocusRing>
    );
  }
);
Button.displayName = "Button";

// Im lazy
export const ButtonLink = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, forwardedRef) => {
    const {
      onPress,
      onClick,
      className,
      initial,
      children,
      isDisabled,
      to,
      dangerouslySetStyle,
      elementType = "button",
      ...delegated
    } = props;
    const controls = useAnimation();
    const ref = useObjectRef(forwardedRef);
    const { hoverProps } = useHover({
      isDisabled,
      onHoverStart: () => {
        controls.stop();
        controls.set({
          background: isDisabled ? buttonColors.disabled : buttonColors.hover,
        });
      },
      onHoverEnd: () => {
        controls.start({
          background: buttonColors.normal,
          transition: { duration: 0.2 },
        });
      },
    });

    const { buttonProps } = useButton(
      {
        onPressStart: () => {
          controls.stop();
          controls.set({ background: buttonColors.press });
        },
        onPressEnd: () => {
          controls.start({
            background: buttonColors.normal,
            transition: { duration: 0.3 },
          });
        },
        onPress: onPress || onClick,
        isDisabled,
        elementType,
      },
      ref
    );

    const cursorHack = isDisabled ? { cursor: "not-allowed" } : {};

    return (
      <FocusRing focusRingClass="ring-2 ring-sky-600/80 ring-offset-4 ring-offset-white/90	">
        <MotionLink
          ref={ref}
          to={to}
          animate={controls}
          {...mergeProps(buttonProps, hoverProps, delegated)}
          style={{
            WebkitTapHighlightColor: "transparent",
            border: "1px solid hsl(0deg 0% 100% / 24%)",
            textShadow: "1px 1px 1px hsl(0deg 0% 10% / 20%)",
            ...dangerouslySetStyle,
            ...cursorHack,
          }}
          className={twMerge(
            "block shrink-0 touch-none select-none",
            "bg-button-normal text-white shadow-sm shadow-black/30",
            "border-transparent focus:border-transparent focus:outline-none",
            "disabled:cursor-not-allowed",
            "text-center",
            isDisabled && "bg-stone-600",
            className
          )}
        >
          {children}
        </MotionLink>
      </FocusRing>
    );
  }
);
ButtonLink.displayName = "ButtonLink";
